import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import Pagination from 'react-bootstrap/Pagination';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getFirestore, orderBy, query } from 'firebase/firestore';
import { app } from '../../firebase';

const RECORDS_PER_PAGE = 50;

function AdmissionFeesComp() {
  const db = getFirestore(app);
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchAdmissionFees = async () => {
      try {
        const paymentsQuery = query(
          collection(db, 'AdmissionFeePayments'),
          orderBy('paymentDate', 'desc')
        );
        const snapshot = await getDocs(paymentsQuery);
        const records = snapshot.docs.map((paymentDoc) => ({
          id: paymentDoc.id,
          ...paymentDoc.data()
        }));

        setPayments(records);
      } catch (error) {
        console.error('Error fetching admission fee payments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdmissionFees();
  }, [db]);

  const formatAmount = useCallback((value, currency = 'KES') => {
    const numericAmount = Number(value || 0);
    return `${currency || 'KES'} ${numericAmount.toLocaleString('en-KE')}`;
  }, []);

  const formatDateTime = useCallback((value) => {
    if (!value) return '-';

    let dateObj = value;
    if (value?.seconds) {
      dateObj = new Date(value.seconds * 1000);
    } else if (!(value instanceof Date)) {
      dateObj = new Date(value);
    }

    if (Number.isNaN(dateObj.getTime())) return '-';
    return `${dateObj.toLocaleDateString()} at ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }, []);

  const filteredPayments = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return payments;

    return payments.filter((payment) => {
      const searchableText = [
        payment.studentName,
        payment.studentEmail,
        payment.phoneNumber,
        payment.course,
        payment.cohort,
        payment.prospectAdmissionNumber,
        payment.paymentReference,
        payment.paymentMethod,
        payment.recordedByEmail
      ].join(' ').toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [payments, searchTerm]);

  const indexOfLastRecord = currentPage * RECORDS_PER_PAGE;
  const indexOfFirstRecord = indexOfLastRecord - RECORDS_PER_PAGE;
  const currentRecords = filteredPayments.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredPayments.length / RECORDS_PER_PAGE);

  const totalCollected = useMemo(() => (
    filteredPayments.reduce((sum, payment) => sum + Number(payment.amount || payment.admissionFeeAmount || 0), 0)
  ), [filteredPayments]);

  const uniqueStudentsCount = useMemo(() => (
    new Set(filteredPayments.map((payment) => payment.userId).filter(Boolean)).size
  ), [filteredPayments]);

  const latestPayment = filteredPayments[0] || null;

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const viewStudent = (userId) => {
    if (!userId) return;
    navigate('/view-student', { state: { myData: userId } });
  };

  const exportCsv = () => {
    const headers = [
      'Payment Date',
      'Student Name',
      'Email',
      'Phone',
      'Course',
      'Admission Ref',
      'Amount',
      'Currency',
      'Payment Method',
      'Payment Reference',
      'Recorded By'
    ];

    const rows = filteredPayments.map((payment) => [
      formatDateTime(payment.paymentDate || payment.createdAt),
      payment.studentName || '',
      payment.studentEmail || '',
      payment.phoneNumber || '',
      payment.course || '',
      payment.prospectAdmissionNumber || '',
      payment.amount || payment.admissionFeeAmount || 0,
      payment.currency || 'KES',
      payment.paymentMethod || '',
      payment.paymentReference || '',
      payment.recordedByEmail || ''
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'admission_fee_payments.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className='clientsComp incomePage admissionFeesPage'>
      <div className="incomeHero">
        <div className="incomeHeroCopy">
          <div className="incomeHeroTitleRow">
            <h5>Admission Fees</h5>
          </div>
          <p className="incomeHeaderMeta">
            <span>{filteredPayments.length} records</span>
            <span className="incomeInlineDot" />
            <span>{uniqueStudentsCount} students</span>
            <span className="incomeInlineDot" />
            <span>Manual and future paybill records</span>
          </p>
        </div>
        <div className="incomeHeroActions">
          <button onClick={exportCsv} className="btn btn-outline-dark" disabled={!filteredPayments.length}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="incomeStatsGrid">
        <div className="incomeStatCard incomeStatPrimary">
          <span className="incomeStatLabel">Admission Fees Collected</span>
          <h3>{formatAmount(totalCollected)}</h3>
          <p>Total from the currently filtered records.</p>
        </div>
        <div className="incomeStatCard">
          <span className="incomeStatLabel">Students Covered</span>
          <h3>{uniqueStudentsCount}</h3>
          <p>Unique applicants with admission-fee records.</p>
        </div>
        <div className="incomeStatCard">
          <span className="incomeStatLabel">This Page</span>
          <h3>{currentRecords.length}</h3>
          <p>Records visible on the current page.</p>
        </div>
        <div className="incomeStatCard">
          <span className="incomeStatLabel">Latest Payment</span>
          <h3>{latestPayment ? formatAmount(latestPayment.amount || latestPayment.admissionFeeAmount, latestPayment.currency) : 'No records'}</h3>
          <p>{latestPayment ? `${latestPayment.studentName || 'Applicant'} • ${formatDateTime(latestPayment.paymentDate || latestPayment.createdAt)}` : 'Admission fee records will appear here.'}</p>
        </div>
      </div>

      <div className="searchContainer incomeToolbar">
        <div className="incomeToolbarMeta">
          <input
            type="text"
            className="form-control admissionFeesSearchInput"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search by name, phone, reference, course..."
          />
        </div>
        <div className="incomeToolbarMeta">
          <span className="incomeToolbarText">Showing {currentRecords.length} of {filteredPayments.length} records</span>
          <span className="incomeInlineDot" />
          <span className="incomeToolbarText">Page {currentPage} of {totalPages || 1}</span>
        </div>
      </div>

      {loading ? (
        <div className="incomeStateCard">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading recorded admission fee payments...</p>
        </div>
      ) : (
        <>
          {filteredPayments.length === 0 ? (
            <div className="incomeStateCard incomeEmptyState">
              <h6>No admission fee records yet</h6>
              <p>Once admission fees are recorded from a student profile, they will appear here for finance review.</p>
            </div>
          ) : (
            <>
              <div className="incomeTableCard">
                <div className="incomeTableHeader">
                  <div>
                    <h6>Recorded Admission Fees</h6>
                    <p>Admission fee payments are kept separate from tuition income for a clean admissions audit trail.</p>
                  </div>
                </div>
                <div className="table-responsive">
                  <Table hover className="align-middle incomeRecordsTable">
                    <thead>
                      <tr>
                        <th>Payment Date</th>
                        <th>Applicant</th>
                        <th>Course</th>
                        <th>Admission Ref</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Reference</th>
                        <th>Recorded By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRecords.map((payment) => (
                        <tr key={payment.id}>
                          <td>
                            <div className="incomeDateCell">
                              <strong>{formatDateTime(payment.paymentDate || payment.createdAt)}</strong>
                              <span>{payment.source || 'manual entry'}</span>
                            </div>
                          </td>
                          <td onClick={() => viewStudent(payment.userId)}>
                            <span className='viewStudentSpan incomeStudentName'>{payment.studentName || 'Unknown Applicant'}</span>
                            <div className="admissionFeesSubText">{payment.phoneNumber || payment.studentEmail || '-'}</div>
                          </td>
                          <td>
                            <span className="incomeCoursePill">{payment.course || '-'}</span>
                          </td>
                          <td>
                            <span className="incomeMethodBadge">{payment.prospectAdmissionNumber || '-'}</span>
                          </td>
                          <td>
                            <span className="incomeAmountText">{formatAmount(payment.amount || payment.admissionFeeAmount, payment.currency)}</span>
                          </td>
                          <td>
                            <span className="incomeMethodBadge">{payment.paymentMethod || '-'}</span>
                          </td>
                          <td>{payment.paymentReference || '-'}</td>
                          <td>
                            <div className="incomeDateCell">
                              <strong>{payment.recordedByEmail || 'System'}</strong>
                              <span>{payment.id}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="incomePaginationWrap">
                  <Pagination>
                    {[...Array(totalPages)].map((_, index) => (
                      <Pagination.Item
                        key={index + 1}
                        active={index + 1 === currentPage}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    ))}
                  </Pagination>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default AdmissionFeesComp;
